import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { useExperiment, EXPERIMENTS } from "@/lib/analytics";
import {
  getConsentVariantDebugInfo,
  resolveConsentVariant,
} from "@/lib/cookies/consent-variant-resolution";
import { CookiePreferencesModal } from "./CookiePreferencesModal";

// ── Control Banner ──────────────────────────────────────────────────────

function ControlBanner({
  onAcceptAll,
  onReject,
  onCustomize,
}: {
  onAcceptAll: () => void;
  onReject: () => void;
  onCustomize: () => void;
}) {
  return (
    <div
      data-testid="consent-banner-control"
      className="fixed bottom-0 left-0 right-0 z-50 p-3 animate-in slide-in-from-bottom duration-500 pointer-events-none"
    >
      <div className="container mx-auto max-w-5xl">
        <div className="bg-card/95 backdrop-blur-md border border-border rounded-xl shadow-lg pointer-events-auto">
          <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 flex items-center gap-3">
              <Cookie className="w-5 h-5 text-primary shrink-0" />
              <p className="text-sm text-muted-foreground">
                We use cookies to improve your experience.{" "}
                <Link to="/privacy" className="text-primary hover:underline">Privacy</Link>
                {" · "}
                <Link to="/cookies" className="text-primary hover:underline">Cookies</Link>
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="ghost" size="sm" onClick={onCustomize}>
                <Settings className="w-4 h-4 mr-1" />
                Customize
              </Button>
              <Button variant="ghost" size="sm" onClick={onReject}>
                Reject
              </Button>
              <Button size="sm" onClick={onAcceptAll}>
                Accept All
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Soft-Wall Variant ───────────────────────────────────────────────────

function SoftWallBanner({
  onAcceptAll,
  onReject,
  onCustomize,
}: {
  onAcceptAll: () => void;
  onReject: () => void;
  onCustomize: () => void;
}) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <div
      data-testid="consent-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center animate-in fade-in duration-300"
    >
      {/* Overlay backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-xs" />

      {/* Centered card */}
      <div className="relative z-10 mx-4 max-w-md w-full animate-in zoom-in-95 duration-300">
        <div className="bg-card border border-border rounded-2xl shadow-2xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Cookie className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-base">Cookie Preferences</h3>
              <p className="text-xs text-muted-foreground">Choose how we use your data</p>
            </div>
          </div>

          <p className="text-sm text-muted-foreground leading-relaxed">
            We use cookies and similar technologies to improve your experience,
            analyze usage, and assist in our marketing efforts. Please make a
            selection to continue.{" "}
            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          </p>

          <div className="flex flex-col gap-2">
            <Button className="w-full" onClick={onAcceptAll}>
              Accept All
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={onReject}>
                Reject
              </Button>
              <Button variant="outline" className="flex-1" onClick={onCustomize}>
                <Settings className="w-4 h-4 mr-1" />
                Customize
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Progressive-Nudge Variant ───────────────────────────────────────────

type NudgePhase = "slim" | "expanded";

function ProgressiveNudgeBanner({
  onAcceptAll,
  onReject,
  onCustomize,
}: {
  onAcceptAll: () => void;
  onReject: () => void;
  onCustomize: () => void;
}) {
  const [phase, setPhase] = useState<NudgePhase>("slim");
  const [shake, setShake] = useState(false);
  const interactedRef = useRef(false);
  const shakeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Phase 3: expand after 15s of inaction
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!interactedRef.current) setPhase("expanded");
    }, 15_000);
    return () => clearTimeout(timeout);
  }, []);

  // Phase 4: shake on first scroll/click while banner is showing
  useEffect(() => {
    function handleInteraction() {
      if (interactedRef.current) return;
      interactedRef.current = true;
      setShake(true);
      shakeTimeoutRef.current = setTimeout(() => setShake(false), 600);
    }
    window.addEventListener("scroll", handleInteraction, { passive: true, once: true });
    window.addEventListener("click", handleInteraction, { once: true });
    return () => {
      window.removeEventListener("scroll", handleInteraction);
      window.removeEventListener("click", handleInteraction);
      if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
    };
  }, []);

  const shakeClass = shake ? "animate-shake" : "";

  if (phase === "slim") {
    return (
      <div
        data-testid="consent-banner-progressive-nudge"
        className={`fixed bottom-0 left-0 right-0 z-50 p-3 animate-in slide-in-from-bottom duration-500 pointer-events-none ${shakeClass}`}
      >
        <div className="container mx-auto max-w-5xl">
          <div className="bg-card/95 backdrop-blur-md border border-border rounded-xl shadow-lg pointer-events-auto">
            <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 flex items-center gap-3">
                <Cookie className="w-5 h-5 text-primary shrink-0" />
                <p className="text-sm text-muted-foreground">
                  We use cookies to improve your experience.{" "}
                  <Link to="/privacy" className="text-primary hover:underline">Privacy</Link>
                  {" · "}
                  <Link to="/cookies" className="text-primary hover:underline">Cookies</Link>
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="ghost" size="sm" onClick={onCustomize}>
                  <Settings className="w-4 h-4 mr-1" />
                  Customize
                </Button>
                <Button variant="ghost" size="sm" onClick={onReject}>
                  Reject
                </Button>
                <Button size="sm" onClick={onAcceptAll}>
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Expanded phase: larger banner + light overlay + pulsing CTA
  return (
    <>
      {/* Light overlay -- does NOT block interaction */}
      <div className="fixed inset-0 z-40 bg-black/20 pointer-events-none animate-in fade-in duration-500" />

      <div
        data-testid="consent-nudge-expanded"
        className={`fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom duration-500 pointer-events-none ${shakeClass}`}
      >
        <div className="container mx-auto max-w-lg">
          <div className="bg-card border border-border rounded-2xl shadow-2xl p-5 space-y-3 pointer-events-auto">
            <div className="flex items-center gap-3">
              <Cookie className="w-5 h-5 text-primary" />
              <p className="text-sm font-medium">
                Please choose your cookie preferences to continue
              </p>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Your choice helps us provide a better experience.{" "}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={onCustomize}>
                <Settings className="w-4 h-4 mr-1" />
                Customize
              </Button>
              <Button variant="outline" size="sm" className="flex-1" onClick={onReject}>
                Reject
              </Button>
              <Button size="sm" className="flex-1 animate-pulse" onClick={onAcceptAll}>
                Accept All
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Main Export ──────────────────────────────────────────────────────────

export const CookieConsentBanner = () => {
  const { search } = useLocation();
  const experimentValue = useExperiment(EXPERIMENTS.COOKIE_CONSENT_VARIANT);
  const variant = useMemo(
    () => resolveConsentVariant(experimentValue, search),
    [experimentValue, search],
  );

  const showDebugOverlay =
    new URLSearchParams(search).get("debug_consent") === "1" || import.meta.env.DEV;
  const debugInfo = useMemo(
    () => getConsentVariantDebugInfo(experimentValue, search),
    [experimentValue, search],
  );

  const {
    showBanner,
    consent,
    acceptAll,
    rejectNonEssential,
    updateConsent,
    openPreferences,
    closePreferences,
    showPreferences,
  } = useCookieConsent(variant);

  const handleCustomize = useCallback(() => {
    openPreferences();
  }, [openPreferences]);

  if (!showBanner && !showPreferences) return null;

  return (
    <>
      {/* While PostHog is still loading, experimentValue is undefined and we intentionally
          render the control banner (resolveConsentVariant → 'control'). experiment_viewed
          only fires once a real variant is known (see useExperiment). */}
      {showDebugOverlay && (
        <div
          className="fixed bottom-16 left-2 z-60 max-w-sm rounded-md border border-border bg-background/95 px-2 py-1 font-mono text-[10px] text-muted-foreground shadow-md"
          data-testid="consent-variant-debug"
        >
          consent: {debugInfo.variant} ({debugInfo.source})
          {experimentValue !== undefined ? ` · ph=${String(experimentValue)}` : " · ph=loading"}
        </div>
      )}
      {showBanner && variant === "control" && (
        <ControlBanner
          onAcceptAll={acceptAll}
          onReject={rejectNonEssential}
          onCustomize={handleCustomize}
        />
      )}

      {showBanner && variant === "soft-wall" && (
        <SoftWallBanner
          onAcceptAll={acceptAll}
          onReject={rejectNonEssential}
          onCustomize={handleCustomize}
        />
      )}

      {showBanner && variant === "progressive-nudge" && (
        <ProgressiveNudgeBanner
          onAcceptAll={acceptAll}
          onReject={rejectNonEssential}
          onCustomize={handleCustomize}
        />
      )}

      <CookiePreferencesModal
        open={showPreferences}
        onClose={closePreferences}
        consent={consent}
        onSave={updateConsent}
        onAcceptAll={acceptAll}
        onRejectNonEssential={rejectNonEssential}
      />
    </>
  );
};
